import { _ReactPixi } from '@inlet/react-pixi';
import { cloneElement, ReactElement } from 'react';

import type { BaseElementProps, Parser, TextElementType } from '../types/text';
import {
  baseTextParser,
  Offset,
} from '../utils/text/baseTextParser/baseTextParser';
import {
  getCachedTokenSize,
  Size,
} from '../utils/text/tokenMeasureCache/tokenMeasureCache';

type Props = BaseElementProps & {
  width?: number;
};

type ParserProps = {
  [K in Extract<keyof Props, 'width'>]: Required<Props>[K];
};

export type EllipsisElementType = ReactElement<
  Omit<Props, 'children'> & {
    children: TextElementType[];
  },
  typeof TextEllipsis
>;

export const createTruncatedTextElement = ({
  element,
  sizes,
  offset,
  maxWidth,
}: {
  element: TextElementType;
  sizes: Size[];
  offset: Offset;
  maxWidth: number;
}): TextElementType => {
  const { props } = element;
  const { text = '' } = props;
  const ellipsisSize = getCachedTokenSize('...', props);
  const truncatePoint = sizes.findIndex(
    (size) => size.width + offset.x + ellipsisSize.width >= maxWidth,
  );

  return cloneElement(element, {
    text: `${text.slice(0, truncatePoint > 0 ? truncatePoint - 1 : 0)}...`,
    position: { x: offset.x, y: offset.y },
  });
};

export const _parseEllipsis: Parser<EllipsisElementType, ParserProps> = (
  element,
  { width, singleSpaceWidth, offset: initOffset },
) => {
  const offset: Offset = { ...initOffset };
  const state = { lineHighestWord: 0 };
  const { children: elements } = element.props;

  const children = baseTextParser({
    offset,
    elements,
    width,
    onTextExceedsWidth: ({ element, sizes }) => {
      const wordSize = sizes[sizes.length - 1];
      const truncatedTextElement = createTruncatedTextElement({
        element,
        sizes,
        offset,
        maxWidth: width,
      });

      const elements = [truncatedTextElement];

      state.lineHighestWord = Math.max(state.lineHighestWord, wordSize.height);

      return { elements, offset };
    },
    onTextFits: ({ element, sizes }) => {
      const wordSize = sizes[sizes.length - 1];

      const elements = [
        cloneElement(element, { position: { x: offset.x, y: offset.y } }),
      ];

      offset.x += (wordSize.width + singleSpaceWidth) | 0;
      state.lineHighestWord = Math.max(state.lineHighestWord, wordSize.height);

      return { elements, offset };
    },
  });

  return {
    children,
    height: offset.y + state.lineHighestWord,
    lines: 1,
  };
};

export function TextEllipsis({ children }: Props): JSX.Element {
  return <>{children}</>;
}
